// Typed Widget Studio admin endpoint functions.
// Backed by the RBAC-gated admin router at /api/v1/admin/widgets/*
// (src/api/app/routers/widget_platform_admin.py, prefix
// /api/v1/admin/widgets). Schemas mirror app/schemas/widget_platform_admin.py.
//
// CHG-2454 — load-bearing FE foundation for the Widget Studio dashboard.
// Every generated Studio screen imports this typed client. It mirrors the
// established dashboard-v2 api-module convention (donors.ts / campaigns.ts /
// kiosks.ts): import `apiClient` from "./client", call .get/.post/.patch with
// paths relative to the /api/v1 baseURL, return the unwrapped .data.
//
// RULE 7 NOTE: the framework prescribes routing generated calls through a
// `withAxiosBehaviors` HOF (web/dashboard/src/lib/v4-client-adapter.ts) for
// 401-refresh / 403-toast / error-normalize / force-logout. That adapter is
// NOT yet present in dashboard-v2 — the existing v2 api modules (donors,
// campaigns, kiosks, tags, …) all route through `apiClient()`, whose response
// interceptor already dispatches the `v2-api-forbidden` CustomEvent on 403
// (see client.ts). This module follows that real, existing convention so it is
// consistent with its siblings; when the v4-client-adapter lands in v2 the
// whole api/ directory migrates together (single cross-cutting CHG), not
// module-by-module. Filed as the v2 axios-behaviors gap (RULE 7 forward-promise).
//
// Wire shapes are snake_case to match the Pydantic schemas. Lifecycle
// transitions (activation_status / registration_status) are DELIBERATELY not
// settable via updateInstance — the backend 400s a smuggled lifecycle field;
// callers must use approve / activate / disable / block.

import { apiClient } from "./client";

// ── Value-space unions (mirror app/models/widget_platform.py enums) ──────────
// Kept as string-literal unions for editor affordance, but every union is
// widened with `(string & {})` so an unrecognized BE value still displays
// rather than breaking the type (same defensive posture as kiosks.ts).
type Loose<T extends string> = T | (string & {});

export type WidgetFamily = Loose<"announcement" | "social_post" | "event" | "donation">;

export type WidgetDefinitionStatus = Loose<"active" | "inactive" | "deprecated">;

export type WidgetSensitivityLevel = Loose<"low" | "medium" | "high" | "payment">;

export type WidgetRegistrationPolicy = Loose<
  | "disabled"
  | "discovery_only"
  | "require_approval"
  | "auto_activate_known_domain"
  | "auto_activate"
>;

export type WidgetInstanceScope = Loose<
  "site" | "page_pattern" | "exact_page" | "content" | "element"
>;

export type WidgetRegistrationSource = Loose<
  "admin" | "embed_first_load" | "api" | "import" | "migration"
>;

export type WidgetRegistrationStatus = Loose<
  | "resolved_existing"
  | "auto_registered"
  | "discovered"
  | "pending_approval"
  | "denied"
  | "duplicate_detected"
>;

export type WidgetActivationStatus = Loose<
  "pending" | "active" | "disabled" | "blocked" | "suspended" | "archived"
>;

export type WidgetContentEntityType = Loose<
  | "announcement"
  | "social_post"
  | "event"
  | "donation_campaign"
  | "designation"
  | "donation_page"
>;

export type WidgetInstallationStatus = Loose<
  "active" | "observed" | "stale" | "removed" | "blocked" | "error"
>;

export type WidgetMountKind = Loose<"script" | "inline" | "iframe" | "server">;

export type WidgetRendererMode = Loose<"web_component" | "iframe" | "server_rendered">;

// ── Shared list envelope ─────────────────────────────────────────────────────
// All admin list endpoints return { items, total, limit, offset } (NOT the
// {items,total} PagedEnvelope used elsewhere — these carry the request window
// back so the Studio ListPage can echo the limit/offset it sent).

export interface WidgetAdminListEnvelope<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
}

// ── Read models (mirror schemas/widget_platform_admin.py) ─────────────────────

export interface WidgetInstanceAdminRead {
  id: string;
  widget_definition_id: string;
  instance_key: string;
  instance_scope: WidgetInstanceScope;
  registration_source: WidgetRegistrationSource;
  registration_status: WidgetRegistrationStatus;
  activation_status: WidgetActivationStatus;
  display_name: string | null;
  default_domain: string | null;
  default_url_pattern: string | null;
  content_binding_entity_type: WidgetContentEntityType | null;
  content_binding_entity_id: string | null;
  first_seen_at: string | null; // ISO 8601
  last_seen_at: string | null;
  activated_at: string | null;
  approved_at: string | null;
  created_at: string | null;
}

export type WidgetInstanceListResponse = WidgetAdminListEnvelope<WidgetInstanceAdminRead>;

export interface WidgetDefinitionRead {
  id: string;
  widget_key: string;
  family: WidgetFamily;
  module: string | null;
  display_name: string;
  type: string;
  default_surface: string;
  sensitivity_level: WidgetSensitivityLevel;
  status: WidgetDefinitionStatus;
  version: string;
  allow_auto_registration: boolean;
  default_registration_policy: WidgetRegistrationPolicy;
  created_at: string | null;
}

export type WidgetDefinitionListResponse = WidgetAdminListEnvelope<WidgetDefinitionRead>;

export interface WidgetInstallationRead {
  id: string;
  widget_instance_id: string;
  widget_definition_id: string;
  domain: string;
  origin: string | null;
  url_pattern: string | null;
  mount_kind: WidgetMountKind;
  surface: string;
  renderer_mode: WidgetRendererMode;
  status: WidgetInstallationStatus;
  error_count: number;
  first_seen_at: string | null;
  last_seen_at: string | null;
  last_rendered_at: string | null;
  created_at: string | null;
}

export type WidgetInstallationListResponse = WidgetAdminListEnvelope<WidgetInstallationRead>;

export interface WidgetPageObservationRead {
  id: string;
  widget_instance_id: string;
  widget_installation_id: string;
  domain: string;
  url_pattern: string | null;
  page_type: string | null;
  render_count: number;
  impression_count: number;
  viewable_count: number;
  click_count: number;
  first_seen_at: string | null;
  last_seen_at: string | null;
  created_at: string | null;
}

export type WidgetPageObservationListResponse =
  WidgetAdminListEnvelope<WidgetPageObservationRead>;

// Family-typed render-data content items (mirror schemas/widget_platform.py).
export interface WidgetRenderDataEventItem {
  id: string;
  title: string;
  date: string | null;
  end_date: string | null;
  description: string | null;
  type: string | null;
  slug: string | null;
  registration_enabled: boolean;
}

export interface WidgetRenderDataAnnouncementItem {
  id: string;
  title: string | null;
  body: string | null;
  image_url: string | null;
  announcement_type: string | null;
  cta_label: string | null;
  cta_url: string | null;
  published_at: string | null;
}

export type WidgetRenderDataContentItem =
  | WidgetRenderDataEventItem
  | WidgetRenderDataAnnouncementItem;

export interface WidgetRenderDataResponse {
  widget_family: WidgetFamily;
  widget_instance_id: string;
  config: Record<string, unknown>;
  content: WidgetRenderDataContentItem[];
  reason: string | null;
  org_timezone: string;
  currency: string;
  generated_at: string; // ISO 8601
}

export interface WidgetPreviewResponse {
  render_allowed: boolean;
  bootstrap: Record<string, unknown> | null;
  render_data: WidgetRenderDataResponse | null;
  reason: string | null;
}

export interface WidgetAnalyticsSummaryResponse {
  // CHG-2499 — ISO code the donation_*_minor_units are denominated in (org default).
  currency: string;
  impressions: number;
  viewable_impressions: number;
  opens: number;
  cta_clicks: number;
  shares: number;
  dismissals: number;
  registrations_started: number;
  registrations_completed: number;
  donations_started: number;
  donations_completed: number;
  donation_gross_minor_units: number;
  donation_net_minor_units: number;
  recurring_donations_completed: number;
  render_errors: number;
  api_errors: number;
}

export interface WidgetEmbedCodeResponse {
  embed_code: string;
}

// ── Request models ────────────────────────────────────────────────────────────

export interface WidgetInstanceCreateRequest {
  // organization_id is NOT a field — it is session-authoritative (resolve_org_id).
  widget_definition_id: string;
  instance_key: string;
  instance_scope: WidgetInstanceScope;
  default_domain?: string | null;
  default_url_pattern?: string | null;
  content_binding_entity_type?: WidgetContentEntityType | null;
  content_binding_entity_id?: string | null;
  display_name?: string | null;
}

// Presentation fields ONLY. activation_status / registration_status are
// deliberately absent — the BE 400s a payload carrying either (use the
// approve / activate / disable / block endpoints instead).
export interface WidgetInstanceUpdateRequest {
  display_name?: string | null;
  default_domain?: string | null;
  default_url_pattern?: string | null;
  content_binding_entity_type?: WidgetContentEntityType | null;
  content_binding_entity_id?: string | null;
  theme_style?: string | null;
  theme_overrides?: Record<string, unknown> | null;
  inherit_org_branding?: boolean | null;
  analytics_enabled?: boolean | null;
  ai_adaptation_enabled?: boolean | null;
}

export interface WidgetInstallationUpdateRequest {
  status?: WidgetInstallationStatus | null;
  metadata_json?: Record<string, unknown> | null;
}

// ── Query param shapes ────────────────────────────────────────────────────────

export interface ListDefinitionsParams {
  family?: WidgetFamily;
  status?: WidgetDefinitionStatus;
  limit?: number;
  offset?: number;
}

export interface ListInstancesParams {
  activation_status?: WidgetActivationStatus;
  registration_status?: WidgetRegistrationStatus;
  limit?: number;
  offset?: number;
}

export interface ListInstallationsParams {
  domain?: string;
  widget_instance_id?: string;
  status?: WidgetInstallationStatus;
  limit?: number;
  offset?: number;
}

export interface ListPageObservationsParams {
  domain?: string;
  widget_instance_id?: string;
  page_type?: string;
  limit?: number;
  offset?: number;
}

// ── Endpoint functions (16 routes) ────────────────────────────────────────────
// Paths are relative to the /api/v1 baseURL configured in client.ts, so the
// /api/v1/admin/widgets router prefix becomes "/admin/widgets/…" here.

const BASE = "/admin/widgets";

// GET /definitions — platform-global WidgetDefinition catalog (not org-scoped).
export async function listDefinitions(
  params: ListDefinitionsParams = {},
): Promise<WidgetDefinitionListResponse> {
  const r = await apiClient().get<WidgetDefinitionListResponse>(`${BASE}/definitions`, {
    params: {
      family: params.family,
      status: params.status,
      limit: params.limit,
      offset: params.offset,
    },
  });
  return r.data;
}

// GET /instances — org's widget instances.
export async function listInstances(
  params: ListInstancesParams = {},
): Promise<WidgetInstanceListResponse> {
  const r = await apiClient().get<WidgetInstanceListResponse>(`${BASE}/instances`, {
    params: {
      activation_status: params.activation_status,
      registration_status: params.registration_status,
      limit: params.limit,
      offset: params.offset,
    },
  });
  return r.data;
}

// POST /instances — create a WidgetInstance row (admin source). NOTE: there is
// NO GET-by-id route on the admin router; instance detail is read via
// listInstances or via preview. createDefinition is intentionally absent —
// POST /definitions is seed-only and not exposed by this router.
export async function createInstance(
  body: WidgetInstanceCreateRequest,
): Promise<WidgetInstanceAdminRead> {
  const r = await apiClient().post<WidgetInstanceAdminRead>(`${BASE}/instances`, body);
  return r.data;
}

// PATCH /instances/{id} — presentation fields only.
export async function updateInstance(
  instanceId: string,
  body: WidgetInstanceUpdateRequest,
): Promise<WidgetInstanceAdminRead> {
  const r = await apiClient().patch<WidgetInstanceAdminRead>(
    `${BASE}/instances/${instanceId}`,
    body,
  );
  return r.data;
}

// GET /instances/{id}/preview — admin preview render config + data.
export async function preview(instanceId: string): Promise<WidgetPreviewResponse> {
  const r = await apiClient().get<WidgetPreviewResponse>(
    `${BASE}/instances/${instanceId}/preview`,
  );
  return r.data;
}

// POST /instances/{id}/approve — discovered/pending -> active.
export async function approve(instanceId: string): Promise<WidgetInstanceAdminRead> {
  const r = await apiClient().post<WidgetInstanceAdminRead>(
    `${BASE}/instances/${instanceId}/approve`,
  );
  return r.data;
}

// POST /instances/{id}/activate.
export async function activate(instanceId: string): Promise<WidgetInstanceAdminRead> {
  const r = await apiClient().post<WidgetInstanceAdminRead>(
    `${BASE}/instances/${instanceId}/activate`,
  );
  return r.data;
}

// POST /instances/{id}/disable.
export async function disable(instanceId: string): Promise<WidgetInstanceAdminRead> {
  const r = await apiClient().post<WidgetInstanceAdminRead>(
    `${BASE}/instances/${instanceId}/disable`,
  );
  return r.data;
}

// POST /instances/{id}/block — optional reason in the body.
export async function block(
  instanceId: string,
  reason?: string | null,
): Promise<WidgetInstanceAdminRead> {
  const r = await apiClient().post<WidgetInstanceAdminRead>(
    `${BASE}/instances/${instanceId}/block`,
    { reason: reason ?? null },
  );
  return r.data;
}

// POST /instances/{id}/embed-code — generate the embed snippet.
export async function embedCode(instanceId: string): Promise<WidgetEmbedCodeResponse> {
  const r = await apiClient().post<WidgetEmbedCodeResponse>(
    `${BASE}/instances/${instanceId}/embed-code`,
  );
  return r.data;
}

// GET /installations — org's widget installations.
export async function listInstallations(
  params: ListInstallationsParams = {},
): Promise<WidgetInstallationListResponse> {
  const r = await apiClient().get<WidgetInstallationListResponse>(`${BASE}/installations`, {
    params: {
      domain: params.domain,
      widget_instance_id: params.widget_instance_id,
      status: params.status,
      limit: params.limit,
      offset: params.offset,
    },
  });
  return r.data;
}

// PATCH /installations/{id} — admin override of status + metadata_json.
export async function updateInstallation(
  installationId: string,
  body: WidgetInstallationUpdateRequest,
): Promise<WidgetInstallationRead> {
  const r = await apiClient().patch<WidgetInstallationRead>(
    `${BASE}/installations/${installationId}`,
    body,
  );
  return r.data;
}

// GET /page-observations — org's per-page observation rows.
export async function listPageObservations(
  params: ListPageObservationsParams = {},
): Promise<WidgetPageObservationListResponse> {
  const r = await apiClient().get<WidgetPageObservationListResponse>(
    `${BASE}/page-observations`,
    {
      params: {
        domain: params.domain,
        widget_instance_id: params.widget_instance_id,
        page_type: params.page_type,
        limit: params.limit,
        offset: params.offset,
      },
    },
  );
  return r.data;
}

// GET /analytics/summary — usage totals. With no argument the BE returns the
// org-wide rollup; passing an instanceId forwards `?instance={id}` so the
// instance-detail status cards scope to a single widget. The `instance` filter
// is forward-promised on the BE (FastAPI tolerates the unknown query param
// today and returns the org-wide rollup until the per-instance filter lands —
// per the widget-instance entity-hint status_card source contract).
export async function analyticsSummary(
  instanceId?: string,
): Promise<WidgetAnalyticsSummaryResponse> {
  const r = await apiClient().get<WidgetAnalyticsSummaryResponse>(`${BASE}/analytics/summary`, {
    params: { instance: instanceId },
  });
  return r.data;
}

// ── Analytics report endpoints (CHG-2459 backend / S8 frontend) ───────────────
// The report sextet (funnel / by-page / by-installation / by-content /
// attribution / export) shares ONE optional query filter (WidgetAnalyticsReportFilter).
// org scope is session-authoritative (resolve_org_id) and is NOT a param.
// SEC-5: the attribution chain derives on the BE from WidgetConversionFold JOIN
// Donation.widget_instance_id ONLY — money fields here are presentation of the
// server-trusted folded totals, never recomputed client-side.

export interface WidgetAnalyticsReportFilter {
  /** Inclusive window start (ISO-8601). Serialized as the `from` query param. */
  from?: string;
  /** Inclusive window end (ISO-8601). */
  to?: string;
  family?: WidgetFamily;
  instance?: string;
  domain?: string;
  limit?: number;
  offset?: number;
}

export interface WidgetAnalyticsCounterSet {
  // CHG-2499 — ISO code the donation_*_minor_units are denominated in (org default).
  currency: string;
  impressions: number;
  viewable_impressions: number;
  opens: number;
  cta_clicks: number;
  shares: number;
  dismissals: number;
  registrations_started: number;
  registrations_completed: number;
  donations_started: number;
  donations_completed: number;
  donation_gross_minor_units: number;
  donation_net_minor_units: number;
  recurring_donations_completed: number;
  render_errors: number;
  api_errors: number;
}

export type WidgetFunnelType =
  | "donation"
  | "event_registration"
  | "announcement"
  | "social_share";

export interface WidgetAnalyticsFunnelStep {
  step: string;
  label: string;
  count: number;
  rate_from_prev: number | null;
}

export interface WidgetAnalyticsFunnelResponse {
  funnel_type: string;
  filter: WidgetAnalyticsReportFilter;
  currency: string; // CHG-2499 — org default for money display on the funnel surface.
  steps: WidgetAnalyticsFunnelStep[];
  generated_at: string;
}

export interface WidgetAnalyticsByPageRow {
  domain: string | null;
  url_pattern: string | null;
  counters: WidgetAnalyticsCounterSet;
}

export interface WidgetAnalyticsByInstallationRow {
  widget_instance_id: string | null;
  domain: string | null;
  surface: string | null;
  placement: string | null;
  counters: WidgetAnalyticsCounterSet;
}

export interface WidgetAnalyticsByContentRow {
  content_binding_entity_type: string | null;
  content_binding_entity_id: string | null;
  campaign_id: string | null;
  event_id_ref: string | null;
  counters: WidgetAnalyticsCounterSet;
}

export interface WidgetAnalyticsAttributionRow {
  widget_instance_id: string | null;
  family: string | null;
  domain: string | null;
  // CHG-2499 — trusted uniform donation currency, else org default. Presentation-only.
  currency: string;
  donations_completed: number;
  donation_gross_minor_units: number;
  donation_net_minor_units: number;
  recurring_donations_completed: number;
}

export interface WidgetAnalyticsGroupedResponse<T> {
  rows: T[];
  filter: WidgetAnalyticsReportFilter;
  // CHG-2499 — org default currency at the envelope (also on every row's counter set).
  currency: string;
  total: number;
  limit: number;
  offset: number;
  generated_at: string;
}

export type WidgetAnalyticsByPageResponse =
  WidgetAnalyticsGroupedResponse<WidgetAnalyticsByPageRow>;
export type WidgetAnalyticsByInstallationResponse =
  WidgetAnalyticsGroupedResponse<WidgetAnalyticsByInstallationRow>;
export type WidgetAnalyticsByContentResponse =
  WidgetAnalyticsGroupedResponse<WidgetAnalyticsByContentRow>;
export type WidgetAnalyticsAttributionResponse =
  WidgetAnalyticsGroupedResponse<WidgetAnalyticsAttributionRow>;

// `from` is a reserved word, so the filter exposes it as a plain key and we map
// it onto the wire `from` param here (the BE aliases the Pydantic field).
function filterParams(f: WidgetAnalyticsReportFilter = {}) {
  return {
    from: f.from,
    to: f.to,
    family: f.family,
    instance: f.instance,
    domain: f.domain,
    limit: f.limit,
    offset: f.offset,
  };
}

// GET /analytics/funnel?funnel_type=… — parameterized funnel.
export async function analyticsFunnel(
  funnelType: WidgetFunnelType,
  filter: WidgetAnalyticsReportFilter = {},
): Promise<WidgetAnalyticsFunnelResponse> {
  const r = await apiClient().get<WidgetAnalyticsFunnelResponse>(`${BASE}/analytics/funnel`, {
    params: { funnel_type: funnelType, ...filterParams(filter) },
  });
  return r.data;
}

// GET /analytics/by-page — grouped by page (domain + url_pattern).
export async function analyticsByPage(
  filter: WidgetAnalyticsReportFilter = {},
): Promise<WidgetAnalyticsByPageResponse> {
  const r = await apiClient().get<WidgetAnalyticsByPageResponse>(`${BASE}/analytics/by-page`, {
    params: filterParams(filter),
  });
  return r.data;
}

// GET /analytics/by-installation — grouped by installation/placement.
export async function analyticsByInstallation(
  filter: WidgetAnalyticsReportFilter = {},
): Promise<WidgetAnalyticsByInstallationResponse> {
  const r = await apiClient().get<WidgetAnalyticsByInstallationResponse>(
    `${BASE}/analytics/by-installation`,
    { params: filterParams(filter) },
  );
  return r.data;
}

// GET /analytics/by-content — grouped by source content entity.
export async function analyticsByContent(
  filter: WidgetAnalyticsReportFilter = {},
): Promise<WidgetAnalyticsByContentResponse> {
  const r = await apiClient().get<WidgetAnalyticsByContentResponse>(`${BASE}/analytics/by-content`, {
    params: filterParams(filter),
  });
  return r.data;
}

// GET /analytics/attribution — SEC-5 trusted conversion attribution.
export async function analyticsAttribution(
  filter: WidgetAnalyticsReportFilter = {},
): Promise<WidgetAnalyticsAttributionResponse> {
  const r = await apiClient().get<WidgetAnalyticsAttributionResponse>(
    `${BASE}/analytics/attribution`,
    { params: filterParams(filter) },
  );
  return r.data;
}

// GET /analytics/export?format=csv — returns the by-installation rows as a
// text/csv attachment Blob (the BE owns the serialization).
export async function analyticsExportCsv(
  filter: WidgetAnalyticsReportFilter = {},
): Promise<Blob> {
  const r = await apiClient().get(`${BASE}/analytics/export`, {
    params: { format: "csv", ...filterParams(filter) },
    responseType: "blob",
  });
  return r.data as Blob;
}

// ── Conversion backfill ops endpoints (DRIFT-879 / CHG-2369) ──────────────────
// Platform-admin, org-scoped, idempotent replay/recovery of trusted revenue the
// event consumer missed. Both require an org context (the BE 400s without one).

export interface ConversionReconcileResult {
  folded: boolean;
}

export interface ConversionReconcileSweepResult {
  scanned: number;
  folded: number;
}

// POST /conversions/reconcile-donation — fold one donation's revenue.
export async function reconcileConversionDonation(
  donationId: string,
): Promise<ConversionReconcileResult> {
  const r = await apiClient().post<ConversionReconcileResult>(
    `${BASE}/conversions/reconcile-donation`,
    { donation_id: donationId },
  );
  return r.data;
}

// POST /conversions/reconcile-sweep — fold this org's unfolded widget donations.
export async function reconcileConversionSweep(
  limit = 100,
): Promise<ConversionReconcileSweepResult> {
  const r = await apiClient().post<ConversionReconcileSweepResult>(
    `${BASE}/conversions/reconcile-sweep`,
    { limit },
  );
  return r.data;
}

export const widgetsAdminApi = {
  listDefinitions,
  listInstances,
  createInstance,
  updateInstance,
  preview,
  approve,
  activate,
  disable,
  block,
  embedCode,
  listInstallations,
  updateInstallation,
  listPageObservations,
  analyticsSummary,
  analyticsFunnel,
  analyticsByPage,
  analyticsByInstallation,
  analyticsByContent,
  analyticsAttribution,
  analyticsExportCsv,
  reconcileConversionDonation,
  reconcileConversionSweep,
};
